import Box from '@mui/material/Box'
import { grey, lightBlue } from '@mui/material/colors'
import { listItemTextClasses } from '@mui/material/ListItemText'
import { styled } from '@mui/material/styles'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'

import { HEADER_HEIGHT, ROW_HEIGHT } from 'components/route-manager/BulkActions/BulkAssignStopsDialog/settings'
import { handleMUIPreventCellKeyDown } from 'utils/datagrid'

const WrappedDataGridPro = ({
  checkboxSelection = true,
  disableChildrenFiltering = true,
  disableSelectionOnClick = true,
  disableColumnReorder = true,
  disableColumnMenu = true,
  hideFooter = true,
  // A disposal or yard stop carries no work order, so the slot is the only id it has.
  getRowId = row => get(row, 'id') ?? get(row, 'routeSlotId'),
  headerHeight = HEADER_HEIGHT,
  rowHeight = ROW_HEIGHT,
  onCellKeyDown = handleMUIPreventCellKeyDown,
  ...rest
}) => {
  const slots = get(rest, 'slots', {})

  const NoRowsOverlay = () => <Box />

  return (
    <DataGridPro
      checkboxSelection={checkboxSelection}
      columnHeaderHeight={headerHeight}
      disableChildrenFiltering={disableChildrenFiltering}
      disableColumnMenu={disableColumnMenu}
      disableColumnReorder={disableColumnReorder}
      disableRowSelectionOnClick={disableSelectionOnClick}
      getRowId={getRowId}
      hideFooter={hideFooter}
      rowHeight={rowHeight}
      slots={{
        noRowsOverlay: NoRowsOverlay,
        ...slots,
      }}
      onCellKeyDown={onCellKeyDown}
      {...rest}
    />
  )
}

WrappedDataGridPro.propTypes = {
  checkboxSelection: PropTypes.bool,
  disableChildrenFiltering: PropTypes.bool,
  disableSelectionOnClick: PropTypes.bool,
  disableColumnReorder: PropTypes.bool,
  disableColumnMenu: PropTypes.bool,
  hideFooter: PropTypes.bool,
  getRowId: PropTypes.func,
  headerHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  onCellKeyDown: PropTypes.func,
}

const BulkMoveStopsDataGridPro = styled(WrappedDataGridPro)(({ theme }) => ({
  border: 0,
  backgroundColor: theme.palette.background.paper,
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaders': {
    border: 0,
    padding: 0,
    borderTop: `${theme.palette.divider} solid 1px`,
    borderBottom: `${theme.palette.divider} solid 1px`,
  },
  '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-row.Mui-selected:hover,  & .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row:hover, ': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-cellCheckbox': {
    paddingTop: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-columnHeader': {
    padding: 0,
  },
  [`& .${gridClasses.cellCheckbox}`]: {
    alignItems: 'center',
  },
  [`& .${gridClasses.row}`]: {
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: `${lightBlue[700]}14`,
    },
  },
  [`& .${gridClasses.row}.from-stop-to-be-assigned`]: {
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: `${theme.palette.warning.light}1F`,
    },
    backgroundColor: `${theme.palette.warning.light}1F`,
  },
  [`& .${gridClasses.row}.from-stop-assigned`]: {
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: `${lightBlue[700]}14`,
    },
    backgroundColor: `${lightBlue[700]}14`,
  },
  [`& .${gridClasses.row}.disabled`]: {
    [`& .${listItemTextClasses.primary}`]: {
      color: theme.palette.text.secondary,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: grey[100],
    },
    backgroundColor: grey[100],
  },
}))

export default BulkMoveStopsDataGridPro
